/**
 * uid → display name for every clinic attribution surface (ZOY-363). The
 * clinic's own personnel list carries displayName (resolved from Auth by
 * the backend, batched per page), so clinic.personnel.read is the ONLY
 * permission a normal role needs to render practitioner names. Auth's
 * member list (clinic.members.manage) remains a FALLBACK for staff without
 * a personnel profile (e.g. reception attribution on admin surfaces). A
 * missing name is stated, never invented — and never a raw uid.
 */

import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { listMembers, type MemberRow } from '@/core/auth/adminApi';
import { listPersonnel, type PersonnelView } from '@/core/clinic/setupApi';
import { useAuthz } from '@/core/authz/AuthzProvider';
import { useSession } from '@/core/auth/SessionProvider';
import { PERM } from '@/core/authz/permissions';

/** Pure resolution, testable: personnel name → member name → "you" → unnamed. */
export function resolveStaffName(args: {
  uid: string;
  personnel: PersonnelView | undefined;
  member: MemberRow | undefined;
  myUid: string | null;
  you: string;
  unnamed: string;
}): string {
  if (args.personnel?.displayName) return args.personnel.displayName;
  if (args.member?.user.displayName) return args.member.user.displayName;
  if (args.member?.user.username) return args.member.user.username;
  if (args.uid === args.myUid) return args.you;
  return args.personnel?.jobTitle ? `${args.unnamed} · ${args.personnel.jobTitle}` : args.unnamed;
}

export function useStaffNames(): {
  nameOf: (uid: string | null | undefined) => string;
  myUid: string | null;
} {
  const intl = useIntl();
  const authz = useAuthz();
  const { session } = useSession();
  const myUid = session.status === 'signedIn' ? session.me.uid : null;

  // ONE cached list (shared key with usePractitioners) — never per-row reads.
  const personnel = useQuery({
    queryKey: ['personnel', 'all', authz.clinicId],
    queryFn: () => listPersonnel({ limit: 100, sort: [{ field: 'uid', dir: 'asc' }] }),
    enabled: authz.can(PERM.personnelRead) && authz.clinicId !== null,
    staleTime: 5 * 60_000,
  });
  const members = useQuery({
    queryKey: ['members', authz.clinicId],
    queryFn: () => listMembers(authz.clinicId!),
    enabled: authz.can(PERM.membersManage) && authz.clinicId !== null,
    staleTime: 10 * 60_000,
  });

  const nameOf = (uid: string | null | undefined): string => {
    if (!uid) return '—';
    return resolveStaffName({
      uid,
      personnel: personnel.data?.items.find((p) => p.uid === uid),
      member: members.data?.find((m) => m.uid === uid),
      myUid,
      you: intl.formatMessage({ id: 'as.you' }),
      unnamed: intl.formatMessage({ id: 'staff.unnamed' }),
    });
  };
  return { nameOf, myUid };
}
